// Redux
import { AsyncResult, Action, ApiAction, Result } from '../..';
import api from '../../redux-api';

// Utils
import { startLoaderAction, stopLoaderAction } from '../ui/loaders';
import { updateInArray } from '../../../utils/immutability';

// Types
import { UserAnnouncementsState, UserAnnouncement } from '../types/announcements';

const INITIAL_STATE: UserAnnouncementsState = {
  announcements: [],
  nextToken: undefined,
  totalCount: 0,
  unacknowledgedCount: 0,
  unreadCount: 0,
  showNotificationCenter: false,
};

// Actions
const SHOW_NOTIFICATION_CENTER: Action = {
  type: 'connect/announcements/user/show-notification-center',
};

const HIDE_NOTIFICATION_CENTER: Action = {
  type: 'connect/announcements/user/hide-notification-center',
};

const GET_ANNOUNCEMENT_COUNTS: ApiAction = {
  type: 'connect/announcements/user/get-counts',
  endpoint: 'users/my-notifications/count',
};

const GET_ANNOUNCEMENTS: ApiAction = {
  type: 'connect/announcements/user/get',
  endpoint: 'users/my-notifications',
};

const PATCH_READ_ANNOUNCEMENTS: ApiAction = {
  type: 'connect/announcements/user/patch-read-announcement',
  endpoint: 'users/my-notifications/read',
};

const PATCH_ACKNOWLEDGE_ANNOUNCEMENT: ApiAction = {
  type: 'connect/announcements/user/patch-acknowledge-announcement',
  endpoint: 'users/my-notifications/:id/acknowledge',
};

export const READ_AND_ACKNOWLEDGE_ANNOUNCEMENT: Action = {
  type: 'connect/announcements/user/read-and-acknowledge-announcement',
};

const MARK_AS_READ: ApiAction = {
  type: 'connect/announcements/user/mark-as-read',
  endpoint: 'users/my-notifications/read',
};

const ANNOUNCEMENTS_QUERY_LIMIT = 20;

export default function reducer(state: UserAnnouncementsState = INITIAL_STATE, action: Action): UserAnnouncementsState {
  switch (action.type) {
    case SHOW_NOTIFICATION_CENTER.type: {
      return {
        ...state,
        showNotificationCenter: true,
      };
    }

    case HIDE_NOTIFICATION_CENTER.type: {
      return {
        ...state,
        showNotificationCenter: false,
      };
    }

    case `${GET_ANNOUNCEMENTS.type}_SUCCESS`: {
      const { notifications, next } = action.data.data;

      return {
        ...state,
        announcements: action.params.next ? state.announcements.concat(notifications) : notifications,
        nextToken: next,
      };
    }

    case `${GET_ANNOUNCEMENT_COUNTS.type}_SUCCESS`: {
      const { totalCount, unacknowledgedCount, unreadCount } = action.data.data;

      return {
        ...state,
        totalCount,
        unacknowledgedCount,
        unreadCount,
      };
    }

    case `${GET_ANNOUNCEMENT_COUNTS.type}_ERROR`: {
      return {
        ...state,
        totalCount: 0,
        unacknowledgedCount: 0,
        unreadCount: 0,
      };
    }

    case `${PATCH_READ_ANNOUNCEMENTS.type}_SUCCESS`: {
      const announcement = state.announcements.find(announcement => announcement._id === action.payload.ids[0]);

      if (!announcement) {
        return state;
      }

      const updatedAnnouncement = {
        ...announcement,
        read: true,
      };

      return {
        ...state,
        announcements: updateInArray<UserAnnouncement>(state.announcements, updatedAnnouncement),
      };
    }

    case `${PATCH_ACKNOWLEDGE_ANNOUNCEMENT.type}_SUCCESS`: {
      const announcement = state.announcements.find(announcement => announcement._id === action.params.id);

      if (!announcement) {
        return state;
      }

      const updatedAnnouncement = {
        ...announcement,
        acknowledged: true,
      };

      return {
        ...state,
        announcements: updateInArray<UserAnnouncement>(state.announcements, updatedAnnouncement),
      };
    }

    default:
      return state;
  }
}

export const getAnnouncementCounts =
  (): AsyncResult<{
    totalCount: number;
    unacknowledgedCount: number;
    unreadCount: number;
  }> =>
  () =>
    api.get(GET_ANNOUNCEMENT_COUNTS).then((res: { data: any }) => res.data);

export const getAnnouncements =
  (query?: string, nextToken?: string): AsyncResult<any> =>
  dispatch => {
    type GetAnnouncementsParams = {
      query?: string;
      next?: string;
      limit?: number;
    };

    const params: GetAnnouncementsParams = {
      limit: ANNOUNCEMENTS_QUERY_LIMIT,
    };

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    return api.get(GET_ANNOUNCEMENTS, params).then((res: { data: any }) => res.data);
  };

export const markAnnouncementsAsRead =
  (ids: string[]): AsyncResult<void> =>
  dispatch =>
    api.patch(MARK_AS_READ, {}, { ids });

export const openNotificationCenter = (): Result<void> => dispatch => {
  dispatch({ ...SHOW_NOTIFICATION_CENTER });
};

export const closeNotificationCenter = (): Result<void> => dispatch => {
  dispatch({ ...HIDE_NOTIFICATION_CENTER });
};

export const readAndAcknowledgeAnnouncement =
  (id: string): AsyncResult<any> =>
  dispatch => {
    dispatch(startLoaderAction(READ_AND_ACKNOWLEDGE_ANNOUNCEMENT.type));

    return api
      .patch(PATCH_READ_ANNOUNCEMENTS, {}, { ids: [id] })
      .then(() => api.patch(PATCH_ACKNOWLEDGE_ANNOUNCEMENT, { id }))
      .finally(() => dispatch(stopLoaderAction(READ_AND_ACKNOWLEDGE_ANNOUNCEMENT.type)));
  };
